import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import Loader from "../../../components/misc/loader";
import { getAdminGenerations } from "../../../services/agenticV2";
import { formatTargetingMode } from "./utils";
import "./index.scss";

function AdminAIGenerations() {
	const [generations, setGenerations] = useState([]);
	const [loading, setLoading] = useState(true);
	const [totalRecords, setTotalRecords] = useState(0);
	const [page, setPage] = useState(1);
	const [pageSize] = useState(25);
	const navigate = useNavigate();

	useEffect(() => {
		document.title = "AI Audience Explorer History";
		fetchGenerations();
	}, [page]);

	const fetchGenerations = async () => {
		setLoading(true);
		try {
			const result = await getAdminGenerations({ page, pageSize });
			setGenerations(result.data || []);
			setTotalRecords(result.total || 0);
		} catch (error) {
			console.error("Error fetching generations:", error);
		} finally {
			setLoading(false);
		}
	};

	const onPageChange = (e) => {
		setPage(Math.floor(e.first / pageSize) + 1);
	};

	const formatDate = (dateStr) => {
		if (!dateStr) return "";
		const d = new Date(dateStr);
		return d.toLocaleDateString("en-US", {
			month: "short",
			day: "numeric",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	const userTemplate = (rowData) => (
		<div className="user-cell">
			<span className="user-name">{rowData.user_name}</span>
			<span className="user-email">{rowData.user_email}</span>
		</div>
	);

	const dateTemplate = (rowData) => formatDate(rowData.created_at);
	const modeTemplate = (rowData) => formatTargetingMode(rowData.targeting_mode);

	const feedbackTemplate = (rowData) => {
		if (rowData.has_feedback) {
			return <Tag value={`Yes (${rowData.feedback_count})`} severity="success" />;
		}
		return <Tag value="None" severity="warning" />;
	};

	const viewTemplate = (rowData) => (
		<button
			className="view-btn"
			onClick={() => navigate(`/admin/ai-generations/${rowData.generation_id}`)}
		>
			View
		</button>
	);

	const header = (
		<div className="table-header">
			<h2>AI Audience Explorer History</h2>
		</div>
	);

	if (loading && generations.length === 0) {
		return (
			<div className="wrapper admin-ai-generations">
				<div className="loading-container">
					<Loader />
					<p>Loading generations...</p>
				</div>
			</div>
		);
	}

	return (
		<div className="wrapper admin-ai-generations">
			<div className="container">
				<DataTable
					value={generations}
					header={header}
					paginator
					rows={pageSize}
					totalRecords={totalRecords}
					lazy
					first={(page - 1) * pageSize}
					onPage={onPageChange}
					loading={loading}
					emptyMessage="No generations found."
					rowHover
					stripedRows
				>
					<Column
						header="User"
						body={userTemplate}
						sortable
						sortField="user_name"
						style={{ minWidth: "180px" }}
					/>
					<Column
						field="silo_name"
						header="Silo"
						sortable
						style={{ minWidth: "120px" }}
					/>
					<Column
						header="Generated On"
						body={dateTemplate}
						sortable
						sortField="created_at"
						style={{ minWidth: "160px" }}
					/>
					<Column
						field="num_audiences"
						header="# Audiences"
						sortable
						style={{ minWidth: "100px", textAlign: "center" }}
					/>
					<Column
						header="Mode"
						body={modeTemplate}
						sortable
						sortField="targeting_mode"
						style={{ minWidth: "120px" }}
					/>
					<Column
						header="Feedback"
						body={feedbackTemplate}
						sortable
						sortField="has_feedback"
						style={{ minWidth: "110px" }}
					/>
					<Column
						header=""
						body={viewTemplate}
						style={{ width: "80px" }}
					/>
				</DataTable>
			</div>
		</div>
	);
}

export default AdminAIGenerations;
